import type { Optional } from 'lib/types/Optional';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY;
// A calendar year or month has no fixed length; the API sends them as 0, so these only keep the sum defined.
const SECONDS_PER_MONTH = 30 * SECONDS_PER_DAY;
const SECONDS_PER_YEAR = 365 * SECONDS_PER_DAY;

const NUMBER = '(\\d+(?:\\.\\d+)?)';
const ISO_DURATION = new RegExp(
  `^P(?:${NUMBER}Y)?(?:${NUMBER}M)?(?:${NUMBER}W)?(?:${NUMBER}D)?(?:T(?:${NUMBER}H)?(?:${NUMBER}M)?(?:${NUMBER}S)?)?$`
);

/**
 * Routine and route durations arrive as ISO 8601 durations, for example `P0Y0M0DT0H15M0S` for 15 minutes.
 * Milliseconds of it; undefined when it is missing or not a duration.
 */
export const parseDurationMs = (value: Optional<string>): number | undefined => {
  const match = value ? ISO_DURATION.exec(value) : null;

  // "P" and "PT" alone are not durations: at least one component is needed.
  if (!match || match.slice(1).every(part => part === undefined)) {
    return undefined;
  }

  const [years, months, weeks, days, hours, minutes, seconds] = match.slice(1).map(part => Number(part ?? 0));
  const totalSeconds =
    years * SECONDS_PER_YEAR +
    months * SECONDS_PER_MONTH +
    weeks * SECONDS_PER_WEEK +
    days * SECONDS_PER_DAY +
    hours * SECONDS_PER_HOUR +
    minutes * SECONDS_PER_MINUTE +
    seconds;

  return Math.round(totalSeconds * MS_PER_SECOND);
};
